import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { twJoin } from 'tailwind-merge';

import Price from 'shopper/components/Price/V2';
import Text from 'shopper/components/Text';

import ErrorBoundary from 'components/ErrorBoundary';
import Image from 'components/Image';
import StopWatch from 'components/StopWatch';
import Store from 'components/Timeline/OfferCard/V2/OfferGridCard/Store';
import Tag from 'components/Timeline/OfferCard/V2/Tag';

import useCountdown from 'hooks/useCountdown';
import useDialog from 'hooks/useDialog';
import useFlashOffer from 'hooks/useFlashOffer';
import useUtmsFromSession from 'hooks/useUtmsFromSession';

import { getShortenedTimeTo } from 'lib/date';
import { sendEvent } from 'lib/gtag';
import {
  getOfferImageLink,
  getUserAvatarUrl,
  toOfferImageAltText,
  toUserAvatarAltText,
} from 'lib/image';
import { toOfferRedirectLink } from 'lib/links';
import { toFormattedOfferOldPrice } from 'lib/offer';
import { toFormattedOfferPrice } from 'lib/offerPage';
import trackEvent from 'lib/posthog/trackEvent';

import Footer from './Footer';

import OFFER from 'constants/offer';

const CouponDialog = dynamic(
  () => import('components/pages/Offer/Main/GoToStoreButton/CouponDialog'),
  { ssr: false }
);

const CardContent = ({
  authors = [],
  cardTitleAs = 'span',
  isOffersShowcaseCard = false,
  offerComments,
  offerImagePriority = false,
  offerLikes,
  offerOldPrice = null,
  offerPhoto,
  offerPrice,
  offerPriceType,
  offerPublished,
  offerStatusName,
  offerTags = [],
  offerTitle,
  storeDomain,
  userId,
  userName,
  userPhoto,
  onImageClickHandler,
  isOfferExclusiveForApp,
  isOfferExclusiveForLoggedUsers,
  offerUserVisibility,
  onTitleClickHandler,
  offerId,
  offerCoupon = null,
  isCarousel = false,
}) => {
  const flashOffer = useFlashOffer(offerId);
  const countdown = useCountdown(flashOffer?.targetDate || new Date(0));
  const { showDialog } = useDialog();
  const { utmParams } = useUtmsFromSession();
  const showStopWatch = Boolean(flashOffer);
  const hasLongOfferOldPrice = Math.abs(offerOldPrice) >= 10000;

  return (
    <>
      <div
        className={twJoin(
          'relative flex gap-5',
          isCarousel ? 'w-full flex-col' : 'size-full flex-row lg:flex-col'
        )}
      >
        <div
          className={twJoin(
            'w-full items-center pb-4',
            isCarousel ? 'flex' : 'hidden lg:flex'
          )}
        >
          <Store storeDomain={storeDomain} />
        </div>
        <div className="relative flex justify-center">
          <div className="group/img overflow-hidden rounded-4">
            <Image
              alt={toOfferImageAltText(offerTitle)}
              className={twJoin(
                'flex size-[120px] self-center rounded-4 transition-transform duration-300 ease-out lg:group-hover/img:scale-[1.08]',
                offerStatusName === OFFER.STATUSES.FINISHED && 'opacity-30'
              )}
              grayscale={offerStatusName === OFFER.STATUSES.FINISHED}
              height={120}
              loading={offerImagePriority ? null : 'lazy'}
              priority={offerImagePriority}
              sizes="120px"
              src={getOfferImageLink(offerPhoto, 120)}
              srcSet={`
                ${getOfferImageLink(offerPhoto, 120)} 120w,
                ${getOfferImageLink(offerPhoto, 180)} 180w,
              `}
              width={120}
              onClick={onImageClickHandler}
            />
          </div>
          {(offerTags.length > 0 ||
            isOfferExclusiveForApp ||
            isOfferExclusiveForLoggedUsers) && (
            <div className="absolute -top-1 left-0 right-0 flex">
              <Tag
                className="px-2 py-1"
                offerTags={offerTags.slice(0, 2)}
                offerUserVisibility={offerUserVisibility}
              />
            </div>
          )}
          {showStopWatch && (
            <div className="absolute bottom-0 left-0 right-0 flex justify-center">
              <ErrorBoundary>
                <StopWatch
                  hours={countdown.hours}
                  minutes={countdown.minutes}
                  phase={flashOffer.phase}
                  seconds={countdown.seconds}
                />
              </ErrorBoundary>
            </div>
          )}
        </div>
        <div
          className={twJoin(
            isCarousel
              ? 'flex w-full flex-col pb-4 lg:ml-0'
              : 'flex size-full flex-col pb-4 lg:ml-0',
            !isOffersShowcaseCard &&
              'justify-between border-b border-neutral-high-300 dark:border-neutral-high-400'
          )}
        >
          <Store
            className={isCarousel ? 'hidden' : 'lg:hidden'}
            storeDomain={storeDomain}
          />
          <div className="flex flex-col">
            <div className="mt-2 flex items-center justify-between lg:mt-1">
              <div className="flex items-center gap-1">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  alt={toUserAvatarAltText(userName)}
                  className="h-5 w-5 rounded-full object-cover"
                  src={getUserAvatarUrl(userPhoto, 36)}
                />
                <span className="max-w-[98px] truncate text-xs text-neutral-high-500 dark:text-neutral-high-400">
                  {userName}
                </span>
              </div>
              <span className="text-xs text-neutral-high-500 dark:text-neutral-high-400">
                {getShortenedTimeTo(offerPublished)}
              </span>
            </div>
            <div className="group/content relative mt-1 overflow-hidden rounded-2">
              <div
                className={twJoin(
                  'min-h-12 w-full',
                  !isOffersShowcaseCard && 'flex items-center'
                )}
              >
                <Text
                  as={cardTitleAs}
                  className={twJoin(
                    'w-full break-words',
                    isOffersShowcaseCard
                      ? 'line-clamp-3 md:line-clamp-2'
                      : 'line-clamp-2'
                  )}
                  onClick={onTitleClickHandler}
                >
                  {offerTitle}
                </Text>
              </div>
              {!isOffersShowcaseCard && (
                <Price
                  className="mt-2"
                  hideSecondaryInfoCents={!isCarousel && hasLongOfferOldPrice}
                  {...(isCarousel && { mobileOrientation: 'row' })}
                  primaryInfo={toFormattedOfferPrice(
                    offerPrice,
                    offerPriceType
                  )}
                  primaryInfoClassName="!text-xl lg:!text-lg"
                  secondaryInfo={toFormattedOfferOldPrice(
                    offerOldPrice,
                    offerPriceType
                  )}
                  secondaryInfoClassName="!text-xs"
                />
              )}
              <div className="absolute inset-0 hidden items-end justify-center bg-neutral-high-100/60 pb-2 opacity-0 backdrop-blur-[2px] transition-opacity duration-300 group-hover/content:opacity-100 dark:bg-neutral-low-500/60 lg:flex">
                {offerCoupon ? (
                  <button
                    className="flex items-center gap-1 rounded-3 bg-primary-300 px-4 py-2 text-sm font-bold !text-neutral-high-100 hover:bg-primary-400 hover:!text-neutral-high-100"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // GA legacy — tracks coupon modal open separately from store click
                      sendEvent({
                        action: 'modal_coupon_open',
                        category: 'active_offer_buy',
                      });
                      // PostHog 'click offer go to store' fires inside CouponDialog on modal CTA
                      showDialog(CouponDialog, {
                        offerCoupon,
                        offerId,
                        storeDomain,
                        utmParams,
                      });
                    }}
                  >
                    Pegar promoção
                  </button>
                ) : (
                  <a
                    className="flex items-center gap-1 rounded-3 bg-primary-300 px-4 py-2 text-sm font-bold !text-neutral-high-100 hover:bg-primary-400 hover:!text-neutral-high-100"
                    href={toOfferRedirectLink(offerId)}
                    rel="noopener noreferrer"
                    target="_blank"
                    onClick={(e) => {
                      e.stopPropagation();
                      trackEvent(
                        'click offer go to store',
                        {
                          offer_id: offerId,
                          store_slug: storeDomain,
                          source: 'home_card_cta',
                        },
                        { destination: 'posthog' }
                      );
                    }}
                  >
                    Pegar promoção
                    <svg
                      fill="none"
                      height="14"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      width="14"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" x2="21" y1="14" y2="3" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {!isOffersShowcaseCard && (
        <Footer
          authors={authors}
          likesCount={offerLikes}
          offerComments={offerComments}
          offerId={offerId}
          offerSlug={null}
          userId={userId}
          userName={userName}
          userPhoto={userPhoto}
        />
      )}
    </>
  );
};

CardContent.propTypes = {
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      username: PropTypes.string.isRequired,
      photo: PropTypes.string.isRequired,
      typeName: PropTypes.string.isRequired,
      specialityName: PropTypes.string,
      level: PropTypes.number.isRequired,
      link: PropTypes.string.isRequired,
    })
  ),
  cardTitleAs: PropTypes.string,
  gaEventCategory: PropTypes.string.isRequired,
  isOfferExclusiveForLoggedUsers: PropTypes.bool,
  isOfferExclusiveForApp: PropTypes.bool,
  isOffersShowcaseCard: PropTypes.bool,
  offerComments: PropTypes.number,
  offerCoupon: PropTypes.string,
  offerId: PropTypes.number.isRequired,
  offerImagePriority: PropTypes.bool,
  offerLikes: PropTypes.number,
  offerOldPrice: PropTypes.number,
  offerPhoto: PropTypes.string.isRequired,
  offerPrice: PropTypes.number.isRequired,
  offerPriceType: PropTypes.string.isRequired,
  offerPublished: PropTypes.string.isRequired,
  offerUserVisibility: PropTypes.string,
  offerStatusName: PropTypes.string.isRequired,
  offerTags: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ),
  offerTitle: PropTypes.string.isRequired,
  storeDomain: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired,
  userName: PropTypes.string.isRequired,
  userPhoto: PropTypes.string,
  onImageClickHandler: PropTypes.func,
  onTitleClickHandler: PropTypes.func,
};

export default CardContent;
